import type {
  SpendingGoalWithProgress,
  SpendingGoalListResponse,
  SpendingGoalAlertHistory,
  CreateSpendingGoalDto,
  UpdateSpendingGoalDto,
  SpendingGoalListQuery,
} from "@repo/share-log-interfaces";
import { z } from "zod";
import type { HttpClient } from "../http";
import type { DataAndMessageResponse, MessageResponse } from "../types";

// Schemas
const MonthlyProgressSchema = z.object({
  month: z.number(),
  amount: z.number(),
  percent: z.number(),
});

const SpendingGoalWithProgressSchema: z.ZodType<SpendingGoalWithProgress> =
  z.object({
    id: z.number(),
    workspaceId: z.number(),
    name: z.string(),
    targetAmount: z.number(),
    categoryIds: z.array(z.number()),
    methodIds: z.array(z.number()),
    userIds: z.array(z.number()),
    isActive: z.boolean(),
    alertHistory: z.record(
      z.string(),
      z.object({
        warning: z.boolean().optional(),
        exceeded: z.boolean().optional(),
      }),
    ) as z.ZodType<SpendingGoalAlertHistory>,
    creatorId: z.number(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    creator: z.object({
      id: z.number(),
      nickname: z.string().nullish(),
      avatarUrl: z.string().nullish(),
    }),
    currentAmount: z.number(),
    progressPercent: z.number(),
    currentMonth: MonthlyProgressSchema,
    previousMonth: MonthlyProgressSchema,
  });

const SpendingGoalListResponseSchema: z.ZodType<SpendingGoalListResponse> =
  z.object({
    goals: z.array(SpendingGoalWithProgressSchema),
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    hasMore: z.boolean(),
  });

// Response Schemas
const CreateSpendingGoalResponseSchema: z.ZodType<
  DataAndMessageResponse<SpendingGoalWithProgress>
> = z.object({
  data: SpendingGoalWithProgressSchema,
  message: z.string(),
});

const GetSpendingGoalsResponseSchema: z.ZodType<
  DataAndMessageResponse<SpendingGoalListResponse>
> = z.object({
  data: SpendingGoalListResponseSchema,
  message: z.string(),
});

const GetSpendingGoalResponseSchema: z.ZodType<
  DataAndMessageResponse<SpendingGoalWithProgress>
> = z.object({
  data: SpendingGoalWithProgressSchema,
  message: z.string(),
});

const UpdateSpendingGoalResponseSchema: z.ZodType<
  DataAndMessageResponse<SpendingGoalWithProgress>
> = z.object({
  data: SpendingGoalWithProgressSchema,
  message: z.string(),
});

const DeleteSpendingGoalResponseSchema: z.ZodType<MessageResponse> = z.object({
  message: z.string(),
});

export function createSpendingGoalModule(httpClient: HttpClient) {
  return {
    create: async (workspaceId: number, data: CreateSpendingGoalDto) => {
      const response = await httpClient.post<
        typeof CreateSpendingGoalResponseSchema
      >(`/api/v1/workspaces/${workspaceId}/spending-goals`, {
        data,
        schema: CreateSpendingGoalResponseSchema,
      });
      return response.data;
    },

    list: async (workspaceId: number, query?: SpendingGoalListQuery) => {
      const params = new URLSearchParams();
      if (query?.isActive !== undefined)
        params.set("isActive", String(query.isActive));
      if (query?.page) params.set("page", String(query.page));
      if (query?.limit) params.set("limit", String(query.limit));

      const queryString = params.toString();
      const url = `/api/v1/workspaces/${workspaceId}/spending-goals${queryString ? `?${queryString}` : ""}`;

      const response = await httpClient.get<
        typeof GetSpendingGoalsResponseSchema
      >(url, {
        schema: GetSpendingGoalsResponseSchema,
      });
      return response.data;
    },

    get: async (workspaceId: number, goalId: number) => {
      const response = await httpClient.get<
        typeof GetSpendingGoalResponseSchema
      >(`/api/v1/workspaces/${workspaceId}/spending-goals/${goalId}`, {
        schema: GetSpendingGoalResponseSchema,
      });
      return response.data;
    },

    update: async (
      workspaceId: number,
      goalId: number,
      data: UpdateSpendingGoalDto,
    ) => {
      const response = await httpClient.patch<
        typeof UpdateSpendingGoalResponseSchema
      >(`/api/v1/workspaces/${workspaceId}/spending-goals/${goalId}`, {
        data,
        schema: UpdateSpendingGoalResponseSchema,
      });
      return response.data;
    },

    remove: async (workspaceId: number, goalId: number) => {
      const response = await httpClient.delete<
        typeof DeleteSpendingGoalResponseSchema
      >(`/api/v1/workspaces/${workspaceId}/spending-goals/${goalId}`, {
        schema: DeleteSpendingGoalResponseSchema,
      });
      return response;
    },
  };
}
